import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'

import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'
import { useUserStore } from '../stores/user-store'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'dashboard' },
  },
  {
    name: 'intro',
    path: '/',
    component: () => import('../pages/auth/Intro.vue'),
    meta: { requiresAuth: false },
  },

  // Auth routes
  {
    path: '/auth',
    component: AuthLayout,
    meta: { requiresAuth: false },
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/Signup.vue'),
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },

  // App routes
  {
    path: '/app',
    component: AppLayout,
    meta: { requiresAuth: false },
    children: [
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'add-measure',
        path: 'add-measure',
        component: () => import('../pages/admin/addMeasurement/AddMeasurement.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'edit-measure',
        path: 'edit-measure/:id',
        component: () => import('../pages/admin/editMeasurement/EditMeasurement.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'my-evolution',
        path: 'my-evolution',
        component: () => import('../pages/admin/myEvolution/MyEvolution.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'history',
        path: 'history',
        component: () => import('../pages/admin/history/History.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'preferences',
        path: 'preferences',
        component: () => import('../pages/admin/preferences/Preferences.vue'),
        meta: { requiresAuth: true },
      },
    ],
  },

  // Terms and conditions
  {
    path: '/terms-conditions',
    name: 'terms-conditions',
    component: () => import('../pages/TermsConditions.vue'),
  },

  // 404
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

router.beforeEach(async (to, from, next) => {
  const userStore = useUserStore()

  if (userStore.token && !userStore.profile) {
    await userStore.fetchUser().catch(() => {
      userStore.logout()
    })
  }

  const isLoggedIn = !!userStore.token

  if (to.path === '/' && isLoggedIn) {
    next({ name: 'dashboard' })
  } else if (to.meta?.requiresAuth === true && !isLoggedIn) {
    next({ name: 'intro' })
  } else {
    next()
  }
})

export default router
